import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'brand' | 'ghost' | 'danger';
export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Solo icona: `aria-label` diventa OBBLIGATORIO (WCAG 4.1.2). */
  readonly iconOnly?: boolean;
  readonly fullWidth?: boolean;
  /** Mostra lo spinner e imposta `aria-busy`. Non disabilita il bottone:
   *  disabilitarlo togliesse il focus, l'utente perderebbe il punto. */
  readonly loading?: boolean;
  readonly startIcon?: ReactNode;
  readonly endIcon?: ReactNode;
}

/**
 * Bottone del design system.
 *
 * Accessibilità garantita dal componente:
 *  · `<button type="button">` per default: senza `type` un bottone dentro un
 *    form fa submit. Era una fonte di bug nel portale statico.
 *  · `iconOnly` senza `aria-label` è un errore in sviluppo, non un difetto
 *    silenzioso.
 *  · Nessuna rimozione dell'outline: il focus ring globale si applica.
 *  · `loading` usa `aria-busy`, non `disabled`: il focus resta dov'è
 *    (WCAG 3.2.2 — nessun cambio di contesto inatteso).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'medium',
    iconOnly = false,
    fullWidth = false,
    loading = false,
    startIcon,
    endIcon,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  if (import.meta.env.DEV && iconOnly && !rest['aria-label'] && !rest['aria-labelledby']) {
    console.error(
      '[Button] iconOnly richiede aria-label o aria-labelledby: senza etichetta ' +
        'il bottone è muto per screen reader (WCAG 4.1.2).',
    );
  }

  const classes = [
    styles.button,
    styles[variant],
    size !== 'medium' ? styles[size] : null,
    iconOnly ? styles.iconOnly : null,
    fullWidth ? styles.fullWidth : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button ref={ref} type={type} className={classes} aria-busy={loading} {...rest}>
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : startIcon}
      {children}
      {endIcon}
    </button>
  );
});
